import { useMemo, useState } from 'react';
import { ChartSection, ChartHeader, ChartError } from '@instamenu/ui-system-next';
import { DailySalesQuery, useDailySalesQuery } from './queries.generated';
import { VictoryAxis, VictoryBar, VictoryGroup, VictoryStack } from 'victory';
import ResponsiveVictoryChart from './ResponsiveVictoryChart';
import Tooltip from './Tooltip';
import {
  formatDate,
  dateFormats,
  formatCurrency,
  formatHour,
  formatTimespan,
} from '@instamenu/formatters';
import { useSalesSummaryContext } from '../../SalesSummaryContext';
import { useLocale } from '@instamenu/context-i18n';
import { palette } from '@instamenu/ui-vanilla-extract';
import { legend, swatch, legendItem, legendLabel } from './DailySales.css';
import { Timespan, comparisonWindow } from '@/components/core/forms/timespans';
import { DateTime } from 'luxon';
import { StatsQuery } from '../../queries.generated';

type ChartDataProps = {
  timespan: Timespan;
  data: NonNullable<NonNullable<StatsQuery['stats'][]>[0]>;
  comparisonData?: DailySalesQuery['stats'];
};

type TooltipData = {
  hour: number;
  tips: number;
  netSales: number;
  xlabel: string;
};

export type TooltipType = {
  index: number;
  data: TooltipData[][];
  labels: string[];
  boundingBox: { x: number; y: number; width: number; height: number };
};

const dayStartHour = 3;
const dayHours = 24;

export default function DailySales() {
  const { i18n } = useLocale();
  const { statsQuery, restaurants, timespan, times } = useSalesSummaryContext();
  const [{ data, error, fetching }] = statsQuery;
  const { start, end } = useMemo(() => comparisonWindow(timespan), [timespan]);
  const comparisonVariables = useMemo(() => {
    return {
      restaurantIds: restaurants?.map(({ id }) => id),
      startDate: start.toISODate(),
      endDate: end.toISODate(),
      ...times,
    };
  }, [restaurants, times, start, end]);

  const [{ data: comparisonData, error: comparisonError, fetching: comparisonLoading }] =
    useDailySalesQuery({
      variables: comparisonVariables,
      pause: !start,
    });

  return (
    <ChartSection
      divide
      Header={
        <ChartHeader
          title={i18n.t('salesSummary.dailySales.dailySales')}
          subtitle={formatTimespan(timespan, i18n)}
        />
      }
      error={error || comparisonError}
      loading={fetching || comparisonLoading}
      empty={!((data?.stats.hourlySales.length ?? 0) > 0)}
    >
      {data ? (
        <ChartData
          data={data.stats}
          comparisonData={start ? comparisonData?.stats : undefined}
          timespan={timespan}
        />
      ) : null}
    </ChartSection>
  );
}

function ChartData({ data, comparisonData, timespan }: ChartDataProps) {
  const { i18n } = useLocale();
  const { start, end } = comparisonWindow(timespan);
  const [tooltip, setTooltip] = useState<TooltipType>();

  try {
    // attach an hour label to each order
    const groups = [comparisonData, data].flatMap((x) => (x ? [x] : []));
    // create an array of hours in the order of the day, starting at 3am
    const orderedHours = Array.from({ length: dayHours }, (_, i) => (i + dayStartHour) % dayHours);
    // bucket the orders by hour
    const bucketedGroups = groups.map(({ hourlySales }) => {
      const buckets = orderedHours.map((hour) => ({
        hour,
        tips: 0,
        netSales: 0,
        xlabel: formatHour(hour),
      }));
      hourlySales.forEach((sale) => {
        const bucketIndex = orderedHours.indexOf(sale.hour);
        buckets[bucketIndex].tips += sale.tips;
        buckets[bucketIndex].netSales += sale.netSales;
      });
      return buckets;
    });

    const handleMouseOver = (props: TooltipType) => setTooltip(props);
    const handleMouseOut = () => setTooltip(undefined);
    const total = data.hourlySales.reduce((sum, hourSales) => sum + hourSales.netSales, 0);
    const comparisonTotal = comparisonData
      ? comparisonData.hourlySales.reduce((sum, hourSales) => sum + hourSales.netSales, 0)
      : 0;
    const delta = comparisonTotal ? 100 * (total / comparisonTotal - 1) : undefined;

    const labelForTimespan = (start: DateTime, end: DateTime) =>
      formatDate(start, dateFormats.dateLong, '', i18n.locale) +
      (start.hasSame(end, 'day')
        ? ''
        : i18n.t('salesSummary.dailySales.to', {
            date: formatDate(end, dateFormats.dateLong, '', i18n.locale),
          }));

    return (
      <div>
        {tooltip && <Tooltip tooltip={tooltip} />}
        {comparisonData && (
          <div className={legend}>
            <div className={legendItem}>
              <div className={swatch} style={{ background: '#797979' }} />
              <div data-id="comparison-legend" className={legendLabel}>
                <span>{labelForTimespan(start, end)}</span>
                <span> ({formatCurrency(comparisonTotal)})</span>
              </div>
            </div>
            <div className={legendItem}>
              <div className={swatch} style={{ background: '#34AC68' }} />
              <div data-id="comparison-legend-2" className={legendLabel}>
                <span>{labelForTimespan(timespan.start, timespan.end)}</span>
                <span> ({formatCurrency(total)}) </span>
                {delta && (
                  <span style={{ color: delta < 0 ? 'red' : '#34AC68' }}>
                    {`${delta < 0 ? '' : '+'}${Math.round(delta)}%`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <ResponsiveVictoryChart minWidth={bucketedGroups[0].length * 50}>
          <VictoryAxis
            style={{
              axis: { stroke: palette.border },
              tickLabels: { fontSize: 12, padding: 10, fontFamily: 'var(--font-gotham)' },
            }}
          />
          <VictoryAxis
            dependentAxis
            style={{
              ticks: { stroke: palette.border, size: 5 },
              grid: { stroke: palette.border },
              axis: { stroke: palette.border },
              tickLabels: {
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: 'var(--font-gotham)',
              },
            }}
            tickFormat={(t) => formatCurrency(t, { cents: false })}
          />
          <VictoryGroup offset={15} style={{ data: { width: 15 } }}>
            {bucketedGroups.map((bucket, index) => (
              <VictoryStack
                key={index}
                colorScale={
                  bucketedGroups.length > 1 && index === 0
                    ? ['#797979', '#c5c5c5']
                    : ['#34ac68', '#93DBB2']
                }
                style={{ data: { cursor: 'pointer' } }}
                events={[
                  {
                    childName: 'all',
                    target: 'data',
                    eventHandlers: {
                      onMouseOut: () => handleMouseOut(),
                      onMouseOver: (event, data) => {
                        if (event.target instanceof Element) {
                          handleMouseOver({
                            index: data.index,
                            data: bucketedGroups,
                            labels: [
                              labelForTimespan(timespan.start, timespan.end),
                              comparisonData && labelForTimespan(start, end),
                            ].flatMap((x) => (x ? [x] : [])),
                            boundingBox: toAbsoluteCoordinates(
                              event.target.getBoundingClientRect(),
                            ),
                          });
                        }
                      },
                    },
                  },
                ]}
              >
                <VictoryBar name="total" data={bucket} x="xlabel" y="netSales" />
                <VictoryBar name="tips" data={bucket} x="xlabel" y="tips" />
              </VictoryStack>
            ))}
          </VictoryGroup>
        </ResponsiveVictoryChart>
      </div>
    );
  } catch (ok) {
    return <ChartError />;
  }
}

const toAbsoluteCoordinates = (rect: DOMRect) => ({
  x: rect.x + window.scrollX,
  y: rect.y + window.scrollY,
  width: rect.width,
  height: rect.height,
});
